import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { internshipsAPI, getImageUrl } from '../../utils/api';
import { FaArrowLeft, FaDownload, FaUser, FaEnvelope, FaPhoneAlt, FaGraduationCap, FaMapMarkerAlt, FaFileAlt } from 'react-icons/fa';

const AdminInternshipDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await internshipsAPI.getById(id);
      if (response.data?.success) {
        setApplication(response.data.data);
      } else {
        setError('Application details not found');
      }
    } catch (err) {
      setError('Error loading application details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setStatusUpdating(true);
      const response = await internshipsAPI.updateStatus(id, newStatus);
      if (response.data?.success) {
        setApplication({ ...application, status: newStatus });
        alert('Status updated successfully');
      } else {
        alert('Failed to update status');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to permanently delete this application?')) {
      try {
        setLoading(true);
        const response = await internshipsAPI.delete(id);
        if (response.data?.success) {
          alert('Application deleted successfully');
          navigate('/admin/internship-applications');
        } else {
          alert('Failed to delete application');
        }
      } catch (err) {
        console.error(err);
        alert('Error deleting application');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading application details...</div>;
  if (error || !application) return <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error || 'Application not found'}</div>;

  return (
    <div style={{ padding: '30px', maxWidth: '1000px', margin: '0 auto' }}>
      <button 
        onClick={() => navigate('/admin/internship-applications')}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontWeight: '600', marginBottom: '25px', fontSize: '15px' }}
      >
        <FaArrowLeft /> Back to Internship Applications
      </button>

      <div style={{ background: '#ffffff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {/* Header Ribbon */}
        <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', padding: '30px', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {application.internship_type} ({application.preferred_mode})
            </span>
            <h1 style={{ fontSize: '28px', fontWeight: '800', marginTop: '10px', marginBottom: '6px' }}>{application.full_name}</h1>
            <p style={{ margin: 0, opacity: 0.9 }}>Application ID: <strong>{application.application_id}</strong></p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>Submitted On</div>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>{new Date(application.created_at).toLocaleString()}</div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '40px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '40px' }}>
            
            {/* Left Column: Details */}
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px', marginBottom: '20px' }}>
                Contact & Education Information
              </h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaEnvelope /> Email</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{application.email}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaPhoneAlt /> Phone</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{application.mobile}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaGraduationCap /> College</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{application.college_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaGraduationCap /> University</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{application.university}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaGraduationCap /> Year / Semester</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{application.current_year}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}><FaMapMarkerAlt /> City</div>
                  <div style={{ fontWeight: '500', color: '#334155' }}>{application.city}</div>
                </div>
              </div>

              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px', marginBottom: '20px' }}>
                Cover Message / Statement
              </h2>
              <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', color: '#475569', fontSize: '15px', lineHeight: '1.6' }}>
                {application.message || 'No message provided by candidate.'}
              </div>
            </div>

            {/* Right Column: Actions & Files */}
            <div>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '25px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '15px' }}>Application Status</h3>
                
                <div style={{ marginBottom: '20px' }}>
                  <select
                    disabled={statusUpdating}
                    value={application.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '600', color: '#334155', background: '#ffffff' }}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Reviewed">Reviewed</option>
                    <option value="Shortlisted">Shortlisted</option>
                    <option value="Selected">Selected</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>

                <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.4' }}>
                  * Changing the status will keep the history updated.
                </div>
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '25px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '15px' }}>Attachments</h3>
                
                {application.resume_file ? (
                  <a 
                    href={getImageUrl(application.resume_file)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: '#3b82f6', color: '#ffffff', textDecoration: 'none', padding: '12px', borderRadius: '8px', fontWeight: '600', transition: 'background-color 0.2s', width: '100%' }}
                  >
                    <FaDownload /> View / Download Resume
                  </a>
                ) : (
                  <div style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500' }}>
                    <FaFileAlt /> Resume not available
                  </div>
                )}
              </div>

              <button 
                onClick={handleDelete}
                style={{ width: '100%', border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', padding: '12px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                Delete Application
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInternshipDetail;
